import { createStreamListener, useStream } from '../../socket/use-stream';
import {
    ProQaNotificationSeverities,
    ProQaSecondaryEventPanelMetadata,
} from '../../socket/stream-types';
import { useCurrentUser } from '../../current-user/current-user';
import { useOpenEventPanel } from '../use-open-event-panel';
import { useCallback, useMemo } from 'react';
import { useToast } from '@cad-arc';
import { v4 } from 'uuid';
import { useIntegratedActionBroadcaster } from '../../action-broadcaster/use-integrated-action-broadcaster';
import { useEnabledFeatures } from '../../common/hooks/enabled-features';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { electronBridge } from '../../electron-bridge/electron-bridge';
import {
    Discipline,
    normalizeDiscipline,
} from '../event-panel-form/helpers/pro-qa-buttons-helpers';

export function ProQaPanelActionsProvider() {
    const currentUser = useCurrentUser();
    const openEventPanel = useOpenEventPanel();
    const toast = useToast();
    const { trigger } = useIntegratedActionBroadcaster();
    const { proQaEnhancementsEnabled } = useEnabledFeatures();
    const { isCadElectron } = useApplicationType();
    const rendererWindowType = electronBridge?.args['--workspace-window-type'];
    const shouldHandleProQaInCurrentWindow =
        !isCadElectron || rendererWindowType === 'EVENT_V2';

    const openSecondaryEventPanel = useCallback(
        (secondaryEvent: ProQaSecondaryEventPanelMetadata, shouldFocusPanel: boolean) => {
            if (typeof secondaryEvent.eventId !== 'number') {
                return;
            }
            openEventPanel({
                eventId: secondaryEvent.eventId,
                shouldFocusPanel,
            });

            const overwritePayload = buildOverwritePayload(secondaryEvent);
            if (!overwritePayload || !shouldHandleProQaInCurrentWindow) {
                return;
            }

            trigger([
                {
                    id: v4(),
                    targetWindowType: 'ROOT',
                    type: 'OVERWRITE_PRO_QA_INCIDENT_NUMBER',
                    payload: overwritePayload,
                },
            ]);
        },
        [openEventPanel, shouldHandleProQaInCurrentWindow, trigger]
    );

    const streamListener = useMemo(
        () =>
            createStreamListener({
                streamNames: ['proqa'] as const,
                onMessage(message) {
                    if (!proQaEnhancementsEnabled) {
                        return;
                    }
                    const proQaEvent = message.payload;
                    if (proQaEvent.actingUserId !== currentUser.userId) {
                        return;
                    }
                    if (proQaEvent.action === 'SHOW_NOTIFICATION') {
                        if (!shouldHandleProQaInCurrentWindow) {
                            return;
                        }
                        const message = proQaEvent.metadata.message;
                        if (typeof message !== 'string' || !message.trim()) {
                            return;
                        }
                        toast({
                            status:
                                proQaEvent.metadata.severity === ProQaNotificationSeverities.ERROR
                                    ? 'error'
                                    : 'default',
                            description: message,
                        });
                        return;
                    }
                    if (proQaEvent.action !== 'OPEN_SECONDARY_EVENT_PANELS') {
                        return;
                    }
                    const shouldFocusPanelValue = proQaEvent.metadata.shouldFocusPanel;
                    const shouldFocusPanel =
                        typeof shouldFocusPanelValue === 'boolean' ? shouldFocusPanelValue : true;

                    const secondaryEvents = proQaEvent.metadata.secondaryEvents;
                    if (!Array.isArray(secondaryEvents) || secondaryEvents.length === 0) {
                        return;
                    }

                    secondaryEvents.forEach((secondaryEvent) => {
                        openSecondaryEventPanel(secondaryEvent, shouldFocusPanel);
                    });
                },
            }),
        [
            currentUser.userId,
            openSecondaryEventPanel,
            proQaEnhancementsEnabled,
            shouldHandleProQaInCurrentWindow,
            toast,
        ]
    );

    useStream(streamListener);

    return <></>;
}

function buildOverwritePayload(
    secondaryEvent: ProQaSecondaryEventPanelMetadata
):
    | {
          type: Discipline;
          incidentNumber: string;
          overIncidentNumber: string;
      }
    | undefined {
    const targetCadDiscipline = normalizeDiscipline(secondaryEvent.targetCadDiscipline);
    const incidentNumber = secondaryEvent.incidentNumber?.trim();
    const overIncidentNumber = secondaryEvent.overIncidentNumber?.trim();

    if (
        !targetCadDiscipline ||
        !incidentNumber ||
        !overIncidentNumber ||
        incidentNumber === overIncidentNumber
    ) {
        return undefined;
    }

    return {
        type: targetCadDiscipline,
        incidentNumber,
        overIncidentNumber,
    };
}
